import React, { useEffect } from 'react';
import { isEqual } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { Button, CircularProgress, Box } from '@mui/material';
import { Update as UpdateIcon, CheckCircle } from '@mui/icons-material';
import { useConfirm } from 'material-ui-confirm';

import { trpc } from '../../api/trpc';
import { ControlBox } from '../../atoms/control-box';
import { useSelector } from '../../store/utils';
import { useTimeExpiration } from '../../utils/use-time-expiration';

type AppUpdateProps = {
  autoCheck?: boolean;
};

export const AppUpdate = ({ autoCheck = false }: AppUpdateProps) => {
  const [updateRequestedAt] = useSelector(
    ({ variables }) =>
      [
        variables.APP_UPDATE_REQUESTED_AT
          ? new Date(variables.APP_UPDATE_REQUESTED_AT)
          : undefined,
      ] as const,
    isEqual
  );

  const updateRequestExpired = useTimeExpiration(
    updateRequestedAt ?? new Date(0),
    300 // 5 minutes
  );

  const updateRequested = Boolean(updateRequestedAt) && !updateRequestExpired;

  const confirm = useConfirm();

  const {
    mutate: checkUpdates,
    isLoading: isCheckingUpdates,
    data: checkUpdatesData,
  } = trpc.updates.check.useMutation();

  const {
    mutate: requestUpdate,
    isLoading: isRequesting,
    data: updateRequestData,
  } = trpc.updates.request.useMutation();

  const handleRequestUpdate = () => {
    confirm({
      title: (
        <FormattedMessage
          id="settings.app_update.confirm.title"
          defaultMessage="Spustit aktualizaci?"
        />
      ),
      description: (
        <FormattedMessage
          id="settings.app_update.confirm.description"
          defaultMessage="Aktualizace může trvat několik minut. Během aktualizace bude aplikace nedostupná a následně se automaticky restartuje."
        />
      ),
      confirmationText: (
        <FormattedMessage
          id="settings.app_update.confirm.confirm"
          defaultMessage="Aktualizovat"
        />
      ),
      cancellationText: (
        <FormattedMessage
          id="settings.app_update.confirm.cancel"
          defaultMessage="Zrušit"
        />
      ),
    })
      .then(() => {
        requestUpdate();
      })
      .catch(() => {
        // noop
      });
  };

  useEffect(() => {
    if (autoCheck && !checkUpdatesData && !isCheckingUpdates) {
      checkUpdates();
    }
  }, [autoCheck]);

  const content = !updateRequested ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {!checkUpdatesData && (
        <Button
          variant="contained"
          onClick={() => checkUpdates()}
          disabled={isCheckingUpdates}
          startIcon={
            isCheckingUpdates ? <CircularProgress size={20} /> : <UpdateIcon />
          }
        >
          <FormattedMessage
            id="settings.app_update.check_button"
            defaultMessage="Zkontrolovat aktualizace"
          />
        </Button>
      )}

      {checkUpdatesData && (
        <Box>
          {'error' in checkUpdatesData ? (
            // failed to check updates
            <>
              <Button
                onClick={() => checkUpdates()}
                disabled={isCheckingUpdates}
                startIcon={
                  isCheckingUpdates ? (
                    <CircularProgress size={20} />
                  ) : (
                    <UpdateIcon />
                  )
                }
                variant="contained"
                color={
                  'errorType' in checkUpdatesData &&
                  checkUpdatesData.errorType === 'network'
                    ? 'warning'
                    : 'error'
                }
              >
                {'errorType' in checkUpdatesData &&
                checkUpdatesData.errorType === 'network' ? (
                  <FormattedMessage
                    id="settings.app_update.error.no_internet"
                    defaultMessage="Zkontrolujte síťové připojení"
                  />
                ) : (
                  checkUpdatesData.error
                )}
              </Button>
            </>
          ) : checkUpdatesData.updateAvailable ? (
            // update available
            <>
              {updateRequestData?.success && (
                // update requested
                <Button
                  variant="outlined"
                  disabled
                  startIcon={<CircularProgress size={18} />}
                >
                  <FormattedMessage
                    id="settings.app_update.requested"
                    defaultMessage="Aktualizace byla vyžádána"
                  />
                </Button>
              )}

              {updateRequestData?.error && (
                // update request error
                <Button
                  onClick={handleRequestUpdate}
                  disabled={isRequesting}
                  variant="contained"
                  color={
                    'errorType' in updateRequestData &&
                    updateRequestData.errorType === 'network'
                      ? 'warning'
                      : 'error'
                  }
                  startIcon={
                    isRequesting ? (
                      <CircularProgress size={20} />
                    ) : (
                      <UpdateIcon />
                    )
                  }
                >
                  {'errorType' in updateRequestData &&
                  updateRequestData.errorType === 'network' ? (
                    <FormattedMessage
                      id="settings.app_update.error.no_internet"
                      defaultMessage="Zkontrolujte síťové připojení"
                    />
                  ) : (
                    updateRequestData.error
                  )}
                </Button>
              )}

              {!updateRequestData && (
                <Button
                  variant="contained"
                  onClick={handleRequestUpdate}
                  disabled={isRequesting}
                  startIcon={
                    isRequesting ? (
                      <CircularProgress size={16} />
                    ) : (
                      <UpdateIcon />
                    )
                  }
                  color="success"
                >
                  <FormattedMessage
                    id="settings.app_update.request_button"
                    defaultMessage="Aktualizace je k dispozici"
                  />
                </Button>
              )}
            </>
          ) : (
            // up to date
            <Button
              onClick={() => checkUpdates()}
              startIcon={<CheckCircle />}
              variant="outlined"
              color="success"
            >
              <FormattedMessage
                id="settings.app_update.up_to_date"
                defaultMessage="Aplikace je aktuální"
              />
            </Button>
          )}
        </Box>
      )}
    </Box>
  ) : (
    // update requested and in progress
    <Button
      variant="outlined"
      disabled
      startIcon={<CircularProgress size={18} />}
    >
      <FormattedMessage
        id="settings.app_update.in_progress"
        defaultMessage="Aktualizace probíhá"
      />
    </Button>
  );

  return (
    <ControlBox
      title={
        <FormattedMessage
          id="settings.app_update.title"
          defaultMessage="Aktualizace aplikace"
        />
      }
      subtitle={
        <FormattedMessage
          id="settings.app_update.subtitle"
          defaultMessage="Zkontrolujte dostupnost nových verzí"
        />
      }
      fullWidth
      control={content}
    />
  );
};
